import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Wallet, ExternalLink, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const input = 'w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white text-sm focus:border-emerald-500 outline-none';

export const CashOutPanel = ({ check, claimToken, onCashed }) => {
  const [form, setForm] = useState({ to_address: '', name: '', email: '', wallet_type: 'Manual entry' });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const connect = async () => {
    if (!window.ethereum) { toast.error('No wallet app detected. Open this link inside MetaMask or Trust Wallet, or paste your address.'); return; }
    try {
      const [account] = await window.ethereum.request({ method: 'eth_requestAccounts' });
      setForm(f => ({ ...f, to_address: account, wallet_type: window.ethereum.isTrust ? 'Trust Wallet' : window.ethereum.isMetaMask ? 'MetaMask' : 'Browser wallet' }));
    } catch { toast.error('Wallet connection was cancelled'); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await axios.post(`${API}/crypto-check/${check.check_number}/cash-out`, { ...form, claim_token: claimToken });
      setResult(res.data.redemption);
      onCashed();
    } catch (err) { toast.error(err.response?.data?.detail || 'Cash-out failed'); }
    finally { setBusy(false); }
  };

  if (result) {
    return (
      <div className="border-t border-slate-700 pt-5 mt-5" data-testid="cash-out-success">
        <p className="text-emerald-400 font-semibold flex items-center gap-2"><CheckCircle className="w-5 h-5" /> {result.beth_amount.toLocaleString()} BETH sent to your wallet</p>
        <code className="block text-xs text-slate-400 break-all mt-2">{result.to_address}</code>
        <a href={result.etherscan_url} target="_blank" rel="noopener noreferrer" className="text-emerald-400 text-sm underline flex items-center gap-1 mt-2" data-testid="cash-out-etherscan-link">View transfer on Etherscan <ExternalLink className="w-3 h-3" /></a>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="border-t border-slate-700 pt-5 mt-5 space-y-3" data-testid="cash-out-form">
      <p className="text-white font-semibold">Transfer this check to your crypto wallet</p>
      <p className="text-slate-400 text-xs">Funds are sent immediately on Sepolia/Mainnet. The receiving wallet and device details are recorded for accountability.</p>
      <button type="button" onClick={connect} data-testid="cash-out-connect-wallet-btn" className="w-full px-4 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2"><Wallet className="w-4 h-4" /> Connect MetaMask / Trust Wallet</button>
      <input required value={form.to_address} onChange={(e) => setForm({ ...form, to_address: e.target.value, wallet_type: 'Manual entry' })} placeholder="Or paste 0x address" className={`${input} font-mono`} data-testid="cash-out-address-input" />
      <div className="grid grid-cols-2 gap-2">
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className={input} data-testid="cash-out-name-input" />
        <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className={input} data-testid="cash-out-email-input" />
      </div>
      <button type="submit" disabled={busy} data-testid="cash-out-submit-btn" className="w-full px-4 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-semibold disabled:opacity-50">{busy ? 'Sending...' : `Transfer ${check.beth_amount.toLocaleString()} BETH now`}</button>
    </form>
  );
};
